// After scripts/migrate-imagekit-to-r2.cjs runs, every ImageKit URL has been
// uploaded to R2 under `images/<rest-of-path>`. To swap traffic onto our own
// CDN without rewriting every DB row, we transparently rewrite ImageKit URLs
// at render time. Set VITE_R2_IMAGES_BASE to the cdn host (e.g.
// https://cdn.bouqueh.com); unset it to fall back to ImageKit.
const R2_IMAGES_BASE = (import.meta.env.VITE_R2_IMAGES_BASE || '').replace(/\/+$/, '');

function rewriteImagekitToR2(src: string): string {
  if (!R2_IMAGES_BASE) return src;
  try {
    const url = new URL(src);
    const segments = url.pathname.split('/').filter(Boolean);
    // First segment is the ImageKit account ID; drop it. Skip transforms (tr:...).
    const rest = segments.slice(1).filter((s) => !s.startsWith('tr:')).join('/');
    return `${R2_IMAGES_BASE}/images/${rest}`;
  } catch {
    return src;
  }
}

export function optimizeImageUrl(src: string, width: number, quality = 70): string {
  if (!src) return src;

  // Skip the transform for assets we already pre-sized and pre-WebP-encoded
  // ourselves (the @2x / @3x variants under /public/unsplash/ are exactly the
  // sizes we render). Passing these through Cloudflare's image transform burns
  // an "Images Transformed" credit for zero quality gain.
  if (src.includes('@2x.webp') || src.includes('@3x.webp')) return src;

  if (src.startsWith('/')) {
    const safeWidth = Math.max(1, Math.round(width));
    const safeQuality = Math.min(90, Math.max(40, Math.round(quality)));
    const normalizedPath = src.startsWith('/') ? src : `/${src}`;
    return `/cdn-cgi/image/format=webp,quality=${safeQuality},width=${safeWidth}${normalizedPath}`;
  }

  // R2 / bouqueh CDN — wrap with the Cloudflare image transform so any
  // PNG/JPEG/GIF in the bucket is delivered as WebP to clients that support
  // it. The bot prerender worker doesn't strip these transforms so SEO
  // crawlers see the same fast image as users.
  if (src.includes('cdn.bouqueh.com') || src.includes('bouqueh.com')) {
    try {
      const url = new URL(src);
      // If the URL already includes /cdn-cgi/image/, don't double-wrap.
      if (url.pathname.startsWith('/cdn-cgi/image/')) return src;
      const safeWidth = Math.max(1, Math.round(width));
      const safeQuality = Math.min(90, Math.max(40, Math.round(quality)));
      return `${url.origin}/cdn-cgi/image/format=webp,quality=${safeQuality},width=${safeWidth}${url.pathname}${url.search}`;
    } catch {
      return src;
    }
  }

  if (src.includes('ik.imagekit.io')) {
    // If R2 is configured, rewrite to R2 first — then run the URL through
    // Cloudflare Image Resizing for on-the-fly transforms (same pattern as
    // local /-prefixed assets above).
    if (R2_IMAGES_BASE) {
      const r2Url = rewriteImagekitToR2(src);
      const safeWidth = Math.max(1, Math.round(width));
      const safeQuality = Math.min(90, Math.max(40, Math.round(quality)));
      // /cdn-cgi/image works with absolute origin URLs too.
      return `/cdn-cgi/image/format=webp,quality=${safeQuality},width=${safeWidth}/${r2Url}`;
    }

    // Otherwise keep using ImageKit's own transformations.
    const transform = `tr:w-${Math.max(1, Math.round(width))},q-${quality},f-webp`;
    try {
      const url = new URL(src);
      const normalizedPath = url.pathname.replace(/^\/+/, '/');
      const segments = normalizedPath.split('/').filter(Boolean);
      const cleanedSegments = segments.filter((segment) => !segment.startsWith('tr:'));

      url.pathname = `/${transform}/${cleanedSegments.join('/')}`;
      return url.toString();
    } catch {
      return src;
    }
  }

  if (src.includes('images.unsplash.com')) {
    try {
      const url = new URL(src);
      url.searchParams.set('w', String(Math.max(1, Math.round(width))));
      url.searchParams.set('q', String(quality));
      url.searchParams.set('auto', 'format');
      return url.toString();
    } catch {
      return src;
    }
  }

  return src;
}
